import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, LinearProgress } from '@mui/material';
import { keyframes } from '@mui/system';

// Pulse animation for the logo/icon
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

// Fade animation for messages
const fadeInOut = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  20% {
    opacity: 1;
    transform: translateY(0);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

const loadingMessages = [
  "Connecting to CarConsul knowledge base...",
  "Initializing AI automotive experts...",
  "Loading latest vehicle data...",
  "Extracting market insights...",
  "Preparing personalized recommendations...",
  "Setting up your consultation session...",
  "Almost ready to help you find your perfect car..."
];

interface LoadingScreenProps {
  message?: string;
  showProgress?: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message,
  showProgress = false 
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Rotate through messages every 2.5 seconds
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2500);

    return () => clearInterval(messageInterval);
  }, []);

  // Simulate progress if showProgress is true
  useEffect(() => {
    if (showProgress) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev; // Stop at 90% to avoid completing
          return prev + Math.random() * 15;
        });
      }, 500);

      return () => clearInterval(progressInterval);
    }
  }, [showProgress]);

  const currentMessage = message || loadingMessages[currentMessageIndex];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        padding: 3,
      }}
    >
      {/* Logo/Icon Area */}
      <Box
        sx={{
          animation: `${pulse} 2s ease-in-out infinite`,
          mb: 4,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            CC
          </Typography>
        </Box>
      </Box>

      {/* CarConsul Brand */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          mb: 1,
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        CarConsul
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          mb: 6,
          color: 'rgba(255, 255, 255, 0.7)',
          fontWeight: 300,
        }}
      >
        Your AI Car Buying Assistant
      </Typography>

      {/* Loading Spinner */}
      <CircularProgress
        size={60}
        thickness={3}
        sx={{
          mb: 4,
          '& .MuiCircularProgress-circle': {
            stroke: 'url(#gradient)',
          },
        }}
      />

      {/* SVG Gradient Definition */}
      <svg width="0" height="0">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
      </svg>

      {/* Loading Message */}
      <Box
        sx={{
          minHeight: 60,
          display: 'flex',
          alignItems: 'center',
          mb: showProgress ? 3 : 0,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 400,
            animation: `${fadeInOut} 2.5s ease-in-out infinite`,
            maxWidth: 400,
          }}
        >
          {currentMessage}
        </Typography>
      </Box>

      {/* Progress Bar */}
      {showProgress && (
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mt: 2,
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.875rem',
            }}
          >
            {progress.toFixed(0)}% loaded
          </Typography>
        </Box>
      )}

      {/* Helpful Tip */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 40,
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.5)',
          maxWidth: 300,
          lineHeight: 1.5,
        }}
      >
        💡 Tip: Our AI analyzes thousands of vehicle listings to find you the best deals
      </Typography>
    </Box>
  );
};

export default LoadingScreen;